import { React, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import BasicLayout from "layouts/authentication/components/BasicLayout";
import bgImage from "assets/images/bg-2.jpeg";
import useQuery from "hooks/useQuery";
import { ToastContainer, toast } from "react-toastify";
import CircularProgress from '@mui/material/CircularProgress';
import { AccountCircle, Lock, Visibility, VisibilityOff } from '@mui/icons-material';
import {
    FormControl,
    IconButton,
    InputAdornment,
    TextField,
    Box,
} from '@mui/material';
import { setISODay } from "date-fns";
import logo from "assets/images/Logo-BZU.png";
import '../login/student_login/style.css'
const BASE_URL = process.env.REACT_APP_BASE_URL;

function Login() {
    const [rememberMe, setRememberMe] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isComponentLoading, setIsComponentLoading] = useState(false);
    const handleSetRememberMe = () => setRememberMe(!rememberMe);
    const query = useQuery();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});

    const handleLogin = async (event) => {
        setIsLoading(true);
        event.preventDefault();

        const validationErrors = {};
        if (!email) {
            validationErrors.email = 'Email is required';
        }
        if (!password) {
            validationErrors.password = 'Password is required';
        }
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length > 0) {
            setIsLoading(false);
            return;
        }

        await fetch(`${BASE_URL}/auth/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password, }),
        })
            .then((res) => res.json())
            .then((data) => {
                if (data.token) {
                    // sessionStorage.setItem('token', data.token);
                    // sessionStorage.setItem('user', JSON.stringify(data.user));
                    if (data.user) {
                        // window.location.href = '/admissions/admin-dashboard';
                        setIsLoading(false);
                    } else {
                        toast.error('Invalid credentials');
                        setIsLoading(false);
                    }
                } else {
                    toast.error('Invalid credentials');
                    setIsLoading(false);
                }
            });
    };

    const handleTogglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };
    
    const loginWithToken = async (token) => {
        setIsComponentLoading(true);
      
        try {
          const res = await fetch(`${BASE_URL}/auth/login_with_token`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          });
      
          if (!res.ok) {
            // non-2xx HTTP status
            throw new Error(`Server responded with ${res.status}`);
          }
      
          const data = await res.json();
      
          if (data && data.user) {
            // Store session
            sessionStorage.setItem('token', token);
            sessionStorage.setItem('user', JSON.stringify(data.user));
            sessionStorage.setItem('rolePermissions', JSON.stringify(data.rolePermissions));
            sessionStorage.setItem('department_id',JSON.stringify(data.department_id));
      
            // Redirect based on role (example: role_id 5 = admin)
            if (data.user) {
              window.location.href = '/admissions/admin-dashboard';
            } else {
                toast.error('Invalid credentials, please login again.');
                 window.location.href = 'https://portal.bzu.edu.pk/logout';
            }
          } else {
            // payload wasn't what we expected
            toast.error('Invalid credentials, please login again.');
             window.location.href = 'https://portal.bzu.edu.pk/logout';
          }
        } catch (err) {
          console.error('Login error:', err);
        //   toast.error('An error occurred. Please try again later.');
          window.location.href = 'https://portal.bzu.edu.pk/logout';
        } finally {
          setIsComponentLoading(false);
        }
      };
      
    useEffect(() => {
        const token = query.get("token");
        if(token) {
            loginWithToken(token);
            console.log("Token found in URL:", token);
        } else {
            //get token from local storage
            const localToken = localStorage.getItem('accessToken');
            if(localToken) {
                loginWithToken(localToken);
                console.log("Token found in local storage:", localToken);
            } else {
                setIsComponentLoading(false);
            }
        }
    }, []);

    // Full Page Loader Component
    if (isComponentLoading) {
        return (
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    zIndex: 9999,
                }}
            >
                <CircularProgress size={60} color="info" />
                <MDTypography variant="h6" color="info" mt={2}>
                    Authenticating...
                </MDTypography>
            </Box>
        );
    }

    return (
        <BasicLayout image={bgImage}>
            <ToastContainer />
            <Card sx={{
                margin: "auto",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
            }}>  
                <img
                    src={logo}
                    alt="University Logo"
                    style={{
                        height: "120px",
                        objectFit: "contain",
                        borderRadius: "10px",
                        marginBottom: 30,
                        marginTop: 6,
                    }}
                />
                <MDBox
                    variant="gradient"
                    bgColor="info"
                    borderRadius="lg"
                    coloredShadow="info"
                    mx={2}
                    mt={-3}
                    p={2}
                    mb={1}
                    textAlign="center"
                >
                    <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
                        Admin Login
                    </MDTypography>
                </MDBox>
                <MDBox pt={4} pb={3} px={3}>
                    <MDBox component="form" role="form">
                        <MDBox mb={2}>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        id="email"
                                        label="Email Address"
                                        variant="outlined"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        error={!!errors.email}
                                        helperText={errors.email}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <AccountCircle />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                        </MDBox>
                        <MDBox mb={2}>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        id="password"
                                        label="Password"
                                        type={showPassword ? 'text' : 'password'}
                                        variant="outlined"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        error={!!errors.password}
                                        helperText={errors.password}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Lock />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={handleTogglePasswordVisibility}>
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </FormControl>
                            </Grid>
                        </MDBox>
                        <MDBox display="flex" alignItems="center" ml={-1}>
                            <Switch checked={rememberMe} onChange={handleSetRememberMe} />
                            <MDTypography
                                variant="button"
                                fontWeight="regular"
                                color="text"
                                onClick={handleSetRememberMe}
                                sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
                            >
                                &nbsp;&nbsp;Remember me
                            </MDTypography>
                        </MDBox>
                        <MDBox mt={4} mb={1}>
                            <MDButton variant="gradient" color="info" fullWidth onClick={handleLogin}>
                                {isLoading ? <CircularProgress color="inherit" size={20} /> : 'Sign in'}
                            </MDButton>
                        </MDBox>
                        {/* <MDBox mt={3} mb={1} textAlign="center">
                            <MDTypography variant="button" color="text">
                                Don&apos;t have an account?{" "}
                                <MDTypography
                                    component={Link}
                                    to="/register"
                                    variant="button"
                                    color="info"
                                    fontWeight="medium"
                                    textGradient
                                >
                                    Sign up
                                </MDTypography>
                            </MDTypography>
                        </MDBox> */}
                    </MDBox>
                </MDBox>
            </Card>
        </BasicLayout>
    );
}

export default Login;